import { useSelector } from 'app-redux'
import { selectAudioStreamState } from 'features/audio-streaming/audio-streaming-redux'
import { useAudioCommands } from 'features/audio-streaming/use-audio-commands'
import { selectYouTubeStreamState } from 'features/youtube-streaming/youtube-streaming-redux'
import { useYouTubeCommands } from 'features/youtube-streaming/use-youtube-commands'
import type { MediaProgress } from 'room/common/media-progress'
import { useConsoleMedia } from './presenter-console'
import { useNow } from './use-now'

/** What the console's remote for a film or recording shows and does. */
export type MediaRemote = {
	name: string
	playing: boolean
	positionMs: number
	durationMs: number
	onPlayPause: () => void
	onSeek: (positionMs: number) => void
	onClose: () => void
}

/**
 * The console's remote for the film or recording playing in the presentation
 * window, or `null` while there is none. Its commands are the player's own
 * (`useYouTubeCommands`, `useAudioCommands`). The position is the
 * presentation window's report, moved on by the time since while playing.
 */
export const useMediaRemote = (
	kind: MediaProgress['kind']
): MediaRemote | null => {
	const youtubeCommands = useYouTubeCommands()
	const audioCommands = useAudioCommands()
	const youtube = useSelector(selectYouTubeStreamState)
	const audio = useSelector(selectAudioStreamState)
	const media = useConsoleMedia()
	const stream = kind === 'youtube' ? youtube : audio
	const playing = stream?.state === 'Playing'
	const now = useNow(250, playing).getTime()

	if (!stream) {
		return null
	}
	const commands = kind === 'youtube' ? youtubeCommands : audioCommands
	const reported = media?.kind === kind ? media : undefined
	const positionMs = reported
		? reported.positionMs + (reported.playing ? now - reported.at : 0)
		: stream.offset
	const durationMs = reported?.durationMs ?? 0
	const atMs = Math.floor(Math.min(positionMs, durationMs || positionMs))

	return {
		name: stream.name,
		playing,
		positionMs: atMs,
		durationMs,
		onPlayPause: () => (playing ? commands.pause(atMs) : commands.play(atMs)),
		onSeek: offsetMs => commands.seek(offsetMs, playing),
		onClose: commands.close,
	}
}
